<script>
import { mapGetters } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAlert } from 'dashboard/composables';
import { useDealsStore } from 'dashboard/stores/deals';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import {
  getSortedAgentsByAvailability,
  getAgentsByUpdatedPresence,
} from 'dashboard/helper/agentHelper.js';
import { picoSearch } from '@scmmishra/pico-search';
import MenuItem from './menuItem.vue';
import MenuItemWithSubmenu from './menuItemWithSubmenu.vue';
import wootConstants from 'dashboard/constants/globals';
import AgentLoadingPlaceholder from './agentLoadingPlaceholder.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const MENU = {
  MARK_AS_READ: 'mark-as-read',
  MARK_AS_UNREAD: 'mark-as-unread',
  PRIORITY: 'priority',
  STATUS: 'status',
  SNOOZE: 'snooze',
  AGENT: 'agent',
  TEAM: 'team',
  LABEL: 'label',
  DELETE: 'delete',
  OPEN_NEW_TAB: 'open-new-tab',
  COPY_LINK: 'copy-link',
  ADD_TO_DEAL: 'add-to-deal',
  STAGE: 'stage',
  OWNER_COMPANY: 'owner-company',
};

// "Le mie società": massimo taggabili per deal (allineato al model Deal).
const MAX_OWNER_COMPANIES = 2;

export default {
  components: {
    MenuItem,
    MenuItemWithSubmenu,
    AgentLoadingPlaceholder,
    NextInput,
    Icon,
  },
  props: {
    chatId: {
      type: Number,
      default: null,
    },
    status: {
      type: String,
      default: '',
    },
    hasUnreadMessages: {
      type: Boolean,
      default: false,
    },
    inboxId: {
      type: Number,
      default: null,
    },
    priority: {
      type: String,
      default: null,
    },
    conversationLabels: {
      type: Array,
      default: () => [],
    },
    conversationUrl: {
      type: String,
      default: '',
    },
    allowedOptions: {
      type: Array,
      default: () => [],
    },
    showAddToDeal: {
      type: Boolean,
      default: false,
    },
    // Deal collegati alla conversazione (dal serializer lista): servono per il
    // sottomenu "Cambia fase".
    deals: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    'updateConversation',
    'assignPriority',
    'markAsUnread',
    'markAsRead',
    'assignAgent',
    'assignTeam',
    'assignLabel',
    'removeLabel',
    'deleteConversation',
    'addToDeal',
    'changeStage',
    'setOwnerCompanies',
    'close',
  ],
  setup() {
    const { isAdmin } = useAdmin();
    const dealsStore = useDealsStore();
    return {
      isAdmin,
      dealsStore,
    };
  },
  data() {
    return {
      MENU,
      labelSearchQuery: '',
      ownerCompanySearchQuery: '',
      // Copia locale di "le mie società" del deal: la muto in ottimistico ai toggle così le
      // spunte si aggiornano subito, poi persisto via emit. Rifresca a ogni apertura menu.
      localOwnerCompanies: (
        this.deals?.[0]?.ownerCompanies ||
        this.deals?.[0]?.owner_companies ||
        []
      ).slice(),
      STATUS_TYPE: wootConstants.STATUS_TYPE,
      readOption: {
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.MARK_AS_READ'),
        icon: 'mail',
      },
      unreadOption: {
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.MARK_AS_UNREAD'),
        icon: 'mail-unread',
      },
      statusMenuConfig: [
        {
          key: wootConstants.STATUS_TYPE.RESOLVED,
          label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.RESOLVED'),
          icon: 'checkmark',
        },
        {
          key: wootConstants.STATUS_TYPE.OPEN,
          label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.REOPEN'),
          icon: 'arrow-redo',
        },
        {
          key: wootConstants.STATUS_TYPE.PENDING,
          label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.PENDING'),
          icon: 'book-clock',
        },
      ],
      snoozeOption: {
        key: wootConstants.STATUS_TYPE.SNOOZED,
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.SNOOZE.TITLE'),
        icon: 'snooze',
      },
      priorityConfig: {
        key: MENU.PRIORITY,
        label: this.$t('CONVERSATION.PRIORITY.TITLE'),
        icon: 'warning',
        options: [
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
            key: null,
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.URGENT'),
            key: 'urgent',
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.HIGH'),
            key: 'high',
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM'),
            key: 'medium',
          },
          {
            label: this.$t('CONVERSATION.PRIORITY.OPTIONS.LOW'),
            key: 'low',
          },
        ].filter(item => item.key !== this.priority),
      },
      labelMenuConfig: {
        key: MENU.LABEL,
        icon: 'tag',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_LABEL'),
      },
      agentMenuConfig: {
        key: MENU.AGENT,
        icon: 'person-add',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_AGENT'),
      },
      teamMenuConfig: {
        key: MENU.TEAM,
        icon: 'people-team-add',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ASSIGN_TEAM'),
      },
      deleteOption: {
        key: MENU.DELETE,
        icon: 'delete',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.DELETE'),
      },
      openInNewTabOption: {
        key: MENU.OPEN_NEW_TAB,
        icon: 'open',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.OPEN_IN_NEW_TAB'),
      },
      copyLinkOption: {
        key: MENU.COPY_LINK,
        icon: 'copy',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.COPY_LINK'),
      },
      addToDealOption: {
        key: MENU.ADD_TO_DEAL,
        icon: 'briefcase',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.ADD_TO_DEAL'),
      },
      stageMenuConfig: {
        key: MENU.STAGE,
        icon: 'status',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.CHANGE_STAGE'),
      },
      ownerCompanyMenuConfig: {
        key: MENU.OWNER_COMPANY,
        icon: 'building-bank',
        label: this.$t('CONVERSATION.CARD_CONTEXT_MENU.OWNER_COMPANY'),
      },
    };
  },
  computed: {
    ...mapGetters({
      labels: 'labels/getLabels',
      teams: 'teams/getTeams',
      assignableAgentsUiFlags: 'inboxAssignableAgents/getUIFlags',
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
    }),
    filteredAgentOnAvailability() {
      const agents = this.$store.getters[
        'inboxAssignableAgents/getAssignableAgents'
      ](this.inboxId);
      const agentsByUpdatedPresence = getAgentsByUpdatedPresence(
        agents,
        this.currentUser,
        this.currentAccountId
      );
      const filteredAgents = getSortedAgentsByAvailability(
        agentsByUpdatedPresence
      );
      return filteredAgents;
    },
    assignableAgents() {
      return [
        {
          confirmed: true,
          name: 'None',
          id: null,
          role: 'agent',
          account_id: 0,
          email: 'None',
        },
        ...this.filteredAgentOnAvailability,
      ];
    },
    showSnooze() {
      // Don't show snooze if the conversation is already snoozed/resolved/pending
      return this.status === wootConstants.STATUS_TYPE.OPEN;
    },
    filteredLabels() {
      const labels = this.labelSearchQuery
        ? picoSearch(this.labels, this.labelSearchQuery, ['title'])
        : this.labels;
      // Assigned labels first, keeping each group's existing order.
      const isAssigned = label => this.conversationLabels.includes(label.title);
      return [...labels].sort((a, b) => isAssigned(b) - isAssigned(a));
    },
    // Primo deal collegato: il cambio fase agisce su questo (le chiavi arrivano snake_case
    // dal serializer della lista).
    firstDeal() {
      return this.deals?.[0] || null;
    },
    // Fasi della pipeline del deal, per popolare il sottomenu "Cambia fase".
    dealStages() {
      const deal = this.firstDeal;
      if (!deal) return [];
      const pipelineId = deal.pipelineId ?? deal.pipeline_id;
      const pipeline = (this.dealsStore.pipelines || []).find(
        p => p.id === pipelineId
      );
      return pipeline?.stages || [];
    },
    currentStageId() {
      const deal = this.firstDeal;
      return deal ? (deal.pipelineStageId ?? deal.pipeline_stage_id) : null;
    },
    showStageMenu() {
      return !!this.firstDeal && this.dealStages.length > 0;
    },
    // "Le mie società": taggabili solo se la conversazione ha un deal (il campo vive sul deal).
    showOwnerCompanyMenu() {
      return !!this.firstDeal;
    },
    canAddOwnerCompany() {
      return this.localOwnerCompanies.length < MAX_OWNER_COMPANIES;
    },
    // Voci del sottomenu: le società già taggate (sempre, con spunta) + i nomi già usati
    // altrove non ancora selezionati, filtrati sul testo digitato.
    ownerCompanyItems() {
      const query = this.ownerCompanySearchQuery.trim().toLowerCase();
      const selectedLower = new Set(
        this.localOwnerCompanies.map(name => name.toLowerCase())
      );
      const suggestions = this.dealsStore.ownerCompanySuggestions || [];
      const merged = [
        ...this.localOwnerCompanies,
        ...suggestions.filter(name => !selectedLower.has(name.toLowerCase())),
      ];
      return merged.filter(
        name => !query || name.toLowerCase().includes(query)
      );
    },
    // Mostra "aggiungi «testo»" solo se il testo non è già tra le voci elencate.
    typedOwnerCompanyIsNew() {
      const value = this.ownerCompanySearchQuery.trim().toLowerCase();
      if (!value) return false;
      return !this.ownerCompanyItems.some(name => name.toLowerCase() === value);
    },
  },
  mounted() {
    this.$store.dispatch('inboxAssignableAgents/fetch', [this.inboxId]);
    // Le fasi servono al sottomenu "Cambia fase"; caricale se non già in store.
    if (!this.dealsStore.pipelines?.length) {
      this.dealsStore.fetchPipelines?.().catch(() => {});
    }
    // I deal servono per l'autocomplete "le mie società"; caricali una volta se assenti.
    if (!this.dealsStore.deals?.length) {
      this.dealsStore.fetchDeals?.({}, { silent: true }).catch(() => {});
    }
  },
  methods: {
    isAllowed(keys) {
      if (!this.allowedOptions.length) return true;
      return keys.some(key => this.allowedOptions.includes(key));
    },
    toggleStatus(status, snoozedUntil) {
      this.$emit('updateConversation', status, snoozedUntil);
    },
    async snoozeConversation() {
      await this.$store.dispatch('setContextMenuChatId', this.chatId);
      const ninja = document.querySelector('ninja-keys');
      ninja.open({ parent: 'snooze_conversation' });
    },
    assignPriority(priority) {
      this.$emit('assignPriority', priority);
    },
    deleteConversation() {
      this.$emit('deleteConversation', this.chatId);
    },
    addToDeal() {
      this.$emit('addToDeal', this.chatId);
    },
    changeStage(stageId) {
      if (stageId === this.currentStageId) {
        this.$emit('close');
        return;
      }
      this.$emit('changeStage', stageId);
    },
    isOwnerCompanySelected(name) {
      const lower = name.toLowerCase();
      return this.localOwnerCompanies.some(c => c.toLowerCase() === lower);
    },
    // Toggle di una società sul deal: aggiunge (se sotto il limite) o rimuove, aggiorna la
    // copia locale (spunte immediate) e persiste. Il menu resta aperto per taggarne 2.
    toggleOwnerCompany(name) {
      const value = name.trim();
      if (!value) return;
      if (this.isOwnerCompanySelected(value)) {
        this.localOwnerCompanies = this.localOwnerCompanies.filter(
          c => c.toLowerCase() !== value.toLowerCase()
        );
      } else {
        if (!this.canAddOwnerCompany) return;
        this.localOwnerCompanies = [...this.localOwnerCompanies, value];
      }
      this.$emit('setOwnerCompanies', [...this.localOwnerCompanies]);
    },
    addOwnerCompanyFromInput() {
      const value = this.ownerCompanySearchQuery.trim();
      this.ownerCompanySearchQuery = '';
      if (!value || !this.canAddOwnerCompany) return;
      if (this.isOwnerCompanySelected(value)) return;
      this.localOwnerCompanies = [...this.localOwnerCompanies, value];
      this.$emit('setOwnerCompanies', [...this.localOwnerCompanies]);
    },
    openInNewTab() {
      if (!this.conversationUrl) return;

      const url = `${window.chatwootConfig.hostURL}${this.conversationUrl}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      this.$emit('close');
    },
    async copyConversationLink() {
      if (!this.conversationUrl) return;
      try {
        const url = `${window.chatwootConfig.hostURL}${this.conversationUrl}`;
        await copyTextToClipboard(url);
        useAlert(this.$t('CONVERSATION.CARD_CONTEXT_MENU.COPY_LINK_SUCCESS'));
        this.$emit('close');
      } catch (error) {
        // error
      }
    },
    show(key) {
      // If the conversation status is same as the action, then don't display the option
      // i.e.: Don't show an option to resolve if the conversation is already resolved.
      return this.status !== key;
    },
    generateMenuLabelConfig(option, type = 'text') {
      return {
        key: option.id,
        ...(type === 'icon' && { icon: option.icon }),
        ...(type === 'label' && { color: option.color }),
        ...(type === 'agent' && { thumbnail: option.thumbnail }),
        ...(type === 'agent' && { status: option.availability_status }),
        ...(type === 'text' && { label: option.label }),
        ...(type === 'label' && { label: option.title }),
        ...(type === 'agent' && { label: option.name }),
        ...(type === 'team' && { label: option.name }),
      };
    },
  },
};
</script>

<template>
  <div
    class="p-1 rounded-md shadow-xl bg-n-alpha-3/50 backdrop-blur-[100px] outline-1 outline outline-n-weak/50"
  >
    <template v-if="isAllowed([MENU.MARK_AS_READ, MENU.MARK_AS_UNREAD])">
      <MenuItem
        v-if="!hasUnreadMessages"
        :option="unreadOption"
        variant="icon"
        @click.stop="$emit('markAsUnread')"
      />
      <MenuItem
        v-else
        :option="readOption"
        variant="icon"
        @click.stop="$emit('markAsRead')"
      />
      <hr class="m-1 rounded border-b border-n-weak dark:border-n-weak" />
    </template>
    <template v-if="isAllowed([MENU.STATUS, MENU.SNOOZE])">
      <template v-for="option in statusMenuConfig">
        <MenuItem
          v-if="show(option.key) && isAllowed([MENU.STATUS])"
          :key="option.key"
          :option="option"
          variant="icon"
          @click.stop="toggleStatus(option.key, null)"
        />
      </template>
      <MenuItem
        v-if="showSnooze && isAllowed([MENU.SNOOZE])"
        :option="snoozeOption"
        variant="icon"
        @click.stop="snoozeConversation()"
      />
      <hr class="m-1 rounded border-b border-n-weak dark:border-n-weak" />
    </template>
    <template
      v-if="isAllowed([MENU.PRIORITY, MENU.LABEL, MENU.AGENT, MENU.TEAM])"
    >
      <MenuItemWithSubmenu
        v-if="isAllowed([MENU.PRIORITY])"
        :option="priorityConfig"
      >
        <MenuItem
          v-for="(option, i) in priorityConfig.options"
          :key="i"
          :option="option"
          @click.stop="assignPriority(option.key)"
        />
      </MenuItemWithSubmenu>
      <MenuItemWithSubmenu
        v-if="isAllowed([MENU.LABEL])"
        :option="labelMenuConfig"
        :sub-menu-available="!!labels.length"
      >
        <div class="pb-1 w-[12.5rem]">
          <NextInput
            v-model="labelSearchQuery"
            type="search"
            size="sm"
            class="w-full"
            custom-input-class="!ps-8 !text-xs"
            :placeholder="$t('CONVERSATION.CARD_CONTEXT_MENU.SEARCH_LABELS')"
            @click.stop
            @keydown.stop
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute z-10 -translate-y-1/2 pointer-events-none size-3.5 text-n-slate-10 top-1/2 start-2"
              />
            </template>
          </NextInput>
        </div>
        <div class="overflow-x-hidden overflow-y-auto max-h-[12.5rem]">
          <MenuItem
            v-for="label in filteredLabels"
            :key="label.id"
            :option="generateMenuLabelConfig(label, 'label')"
            :variant="
              conversationLabels.includes(label.title)
                ? 'label-assigned'
                : 'label'
            "
            @mousedown.prevent
            @click.stop="
              conversationLabels.includes(label.title)
                ? $emit('removeLabel', label)
                : $emit('assignLabel', label)
            "
          />
          <p
            v-if="!filteredLabels.length"
            class="px-2 py-2 m-0 text-xs text-center text-n-slate-11"
          >
            {{ $t('CONVERSATION.CARD_CONTEXT_MENU.NO_LABELS_FOUND') }}
          </p>
        </div>
      </MenuItemWithSubmenu>
      <MenuItemWithSubmenu
        v-if="isAllowed([MENU.AGENT])"
        :option="agentMenuConfig"
        :sub-menu-available="!!assignableAgents.length"
      >
        <AgentLoadingPlaceholder v-if="assignableAgentsUiFlags.isFetching" />
        <template v-else>
          <MenuItem
            v-for="agent in assignableAgents"
            :key="agent.id"
            :option="generateMenuLabelConfig(agent, 'agent')"
            variant="agent"
            @click.stop="$emit('assignAgent', agent)"
          />
        </template>
      </MenuItemWithSubmenu>
      <MenuItemWithSubmenu
        v-if="isAllowed([MENU.TEAM])"
        :option="teamMenuConfig"
        :sub-menu-available="!!teams.length"
      >
        <MenuItem
          v-for="team in teams"
          :key="team.id"
          :option="generateMenuLabelConfig(team, 'team')"
          @click.stop="$emit('assignTeam', team)"
        />
      </MenuItemWithSubmenu>
      <hr class="m-1 rounded border-b border-n-weak dark:border-n-weak" />
    </template>
    <template v-if="isAllowed([MENU.OPEN_NEW_TAB, MENU.COPY_LINK])">
      <MenuItem
        v-if="isAllowed([MENU.OPEN_NEW_TAB])"
        :option="openInNewTabOption"
        variant="icon"
        @click.stop="openInNewTab"
      />
      <MenuItem
        v-if="isAllowed([MENU.COPY_LINK])"
        :option="copyLinkOption"
        variant="icon"
        @click.stop="copyConversationLink"
      />
    </template>
    <template v-if="showAddToDeal">
      <hr class="m-1 rounded border-b border-n-weak dark:border-n-weak" />
      <MenuItem
        :option="addToDealOption"
        variant="icon"
        @click.stop="addToDeal"
      />
    </template>
    <template v-if="showStageMenu">
      <hr class="m-1 rounded border-b border-n-weak dark:border-n-weak" />
      <MenuItemWithSubmenu
        :option="stageMenuConfig"
        :sub-menu-available="dealStages.length > 0"
      >
        <MenuItem
          v-for="stage in dealStages"
          :key="stage.id"
          :option="{ key: stage.id, color: stage.color, label: stage.name }"
          :variant="stage.id === currentStageId ? 'label-assigned' : 'label'"
          @click.stop="changeStage(stage.id)"
        />
      </MenuItemWithSubmenu>
    </template>
    <template v-if="showOwnerCompanyMenu">
      <MenuItemWithSubmenu :option="ownerCompanyMenuConfig" sub-menu-available>
        <div class="pb-1 w-[12.5rem]">
          <NextInput
            v-model="ownerCompanySearchQuery"
            type="search"
            size="sm"
            class="w-full"
            custom-input-class="!ps-8 !text-xs"
            :placeholder="
              $t('CONVERSATION.CARD_CONTEXT_MENU.OWNER_COMPANY_SEARCH')
            "
            @click.stop
            @keydown.stop
            @keyup.enter="addOwnerCompanyFromInput"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-building-2"
                class="absolute z-10 -translate-y-1/2 pointer-events-none size-3.5 text-n-slate-10 top-1/2 start-2"
              />
            </template>
          </NextInput>
        </div>
        <div class="overflow-x-hidden overflow-y-auto max-h-[12.5rem]">
          <MenuItem
            v-for="name in ownerCompanyItems"
            :key="name"
            :option="{ key: name, label: name }"
            :variant="
              isOwnerCompanySelected(name) ? 'label-assigned' : 'default'
            "
            @mousedown.prevent
            @click.stop="toggleOwnerCompany(name)"
          />
          <div
            v-if="typedOwnerCompanyIsNew && canAddOwnerCompany"
            class="flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer text-n-slate-11 hover:bg-n-brand hover:text-white"
            @mousedown.prevent
            @click.stop="addOwnerCompanyFromInput"
          >
            <Icon icon="i-lucide-plus" class="size-3.5 shrink-0" />
            <span class="text-xs truncate">
              {{
                $t('CONVERSATION.CARD_CONTEXT_MENU.OWNER_COMPANY_ADD', {
                  name: ownerCompanySearchQuery.trim(),
                })
              }}
            </span>
          </div>
          <p
            v-if="!canAddOwnerCompany"
            class="px-2 py-2 m-0 text-xs text-center text-n-slate-11"
          >
            {{ $t('CONVERSATION.CARD_CONTEXT_MENU.OWNER_COMPANY_MAX') }}
          </p>
          <p
            v-else-if="!ownerCompanyItems.length && !typedOwnerCompanyIsNew"
            class="px-2 py-2 m-0 text-xs text-center text-n-slate-11"
          >
            {{ $t('CONVERSATION.CARD_CONTEXT_MENU.OWNER_COMPANY_EMPTY') }}
          </p>
        </div>
      </MenuItemWithSubmenu>
    </template>
    <template v-if="isAdmin && isAllowed([MENU.DELETE])">
      <hr class="m-1 rounded border-b border-n-weak dark:border-n-weak" />
      <MenuItem
        :option="deleteOption"
        variant="icon"
        @click.stop="deleteConversation"
      />
    </template>
  </div>
</template>
